// Console visibility is a UX policy, not an authorization/security boundary.
// Native browser network/CSP errors and extension output are outside this API.
export const CONSOLE_OUTPUT_METHODS = [
  'assert', 'clear', 'count', 'countReset', 'debug', 'dir', 'dirxml', 'error',
  'group', 'groupCollapsed', 'groupEnd', 'info', 'log', 'table', 'time',
  'timeEnd', 'timeLog', 'timeStamp', 'trace', 'warn', 'profile', 'profileEnd',
];

export function createAdminConsolePolicy(target) {
  let allowed = false;
  let installed = false;
  let generation = 0;
  const originals = new Map();
  const guard = original => (...args) => {
    if (allowed) return original.apply(target, args);
    return undefined;
  };
  return {
    install() {
      if (installed || !target) return;
      installed = true;
      for (const method of CONSOLE_OUTPUT_METHODS) {
        if (typeof target[method] !== 'function') continue;
        const original = target[method];
        originals.set(method, original);
        target[method] = guard(original);
      }
    },
    revoke() {
      // Do not leave a previous Admin's application output visible on logout.
      if (allowed) {
        try { originals.get('clear')?.call(target); } catch { /* Console tools must never break logout. */ }
      }
      allowed = false;
      generation++;
      return generation;
    },
    confirm(user, ticket) {
      if (ticket !== generation) return;
      allowed = Boolean(user?.userDetails?.localAccountId &&
        (user.rolesMapped?.Admin === true || (Array.isArray(user.userRoles) && user.userRoles.includes('Admin'))));
    },
  };
}

export const adminConsole = createAdminConsolePolicy(typeof console === 'undefined' ? null : console);

// Only fresh role-fetch completion can enable output. Persisted roles must not
// unlock startup logs; obsolete requests must not unlock a different session.
export const createAdminConsoleMiddleware = (policy) => () => {
  let requestId;
  let ticket;
  return next => action => {
    if (action.type === 'user/fetchUserRoles/pending') {
      ticket = policy.revoke();
      requestId = action.meta?.requestId;
    } else if (action.type === 'user/clearUserRoles' || action.type === 'persist/REHYDRATE') {
      policy.revoke();
      requestId = undefined;
    } else if (action.type === 'user/fetchUserRoles/rejected' && action.meta?.requestId === requestId) {
      policy.revoke();
      requestId = undefined;
    }
    const result = next(action);
    if (action.type === 'user/fetchUserRoles/fulfilled' && requestId && action.meta?.requestId === requestId) {
      policy.confirm(action.payload, ticket);
      requestId = undefined;
    }
    return result;
  };
};

export const adminConsoleMiddleware = createAdminConsoleMiddleware(adminConsole);
